import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ActionsTable } from 'Common/Components';
import { useSessionStore } from 'Common/Stores';
import { warehouseListI } from 'interfaces';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationType, showNotifications } from 'helpers/toas_notification';
import { useWarehouseStore } from '../store/useWarehouseStore';
import { getWarehouse, removeWarehouse } from 'services/service.cruds/WarehouseServices/WarehouseService';

export const useWarehouseList = () => {
  const { session } = useSessionStore();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const {
    setIsEdit,
    setWarehouse,
    handleOpenModalWarehouse,
  } = useWarehouseStore();

  const [warehouseRows, setWarehouseRows] = useState<any[]>([]);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const columns = [
    {
      header: 'ID',
      accessorKey: 'ID',
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: t('CrudWarehouse.tablesColumn.description'),
      accessorKey: 'description',
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: t('CrudWarehouse.tablesColumn.branch'),
      accessorKey: 'branch',
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: t('CrudWarehouse.tablesColumn.company'),
      accessorKey: 'company',
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: t('CrudWarehouse.tablesColumn.type'),
      accessorKey: 'type',
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: t('correlativeScreen.table.actions'),
      accessorKey: 'actions',
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ getValue }: { getValue: () => any }) => getValue(),
    },
  ];

  const {
    data: warehousesData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['administration-service-warehouses'],
    queryFn: () => {
      return getWarehouse();
    },
  });

  useEffect(() => {
    if (warehousesData) {
      setWarehouseRows(createCorrlativeRows(warehousesData));
    }
  }, [warehousesData]);

  const createCorrlativeRows = (warehouses: warehouseListI[]) => {
    return warehouses.map((warehouse, index) => ({
      ID: index + 1,
      description: warehouse.description,
      branch: warehouse.branch.description,
      company: warehouse.branch.company.name,
      type: warehouse.mainWarehouse === 1 ? "Bodega Principal" : "Bodega Secundaria",
      actions: (
        <ActionsTable
          onClickDelete={() => onClickDelete(warehouse.id)}
          onClickEdith={() => handleEditModal(warehouse)}
        ></ActionsTable>
      ),
    }));
  };

  const handleEditModal = (warehouse: warehouseListI) => {
    setWarehouse(warehouse);
    setIsEdit(true);
    handleOpenModalWarehouse();
  };

  const [OptionData, setOptionData] = useState<number>(0);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);
  
  const onClickDelete = (id: number) => {
    setDeleteModal(true);
    if (id) {
      setOptionData(id);
    }
  };

  const onDeleteConfirm = async () => {
    if (isDeleting) return;
    setIsDeleting(true);

    try {
      const responseDelete = await removeWarehouse(OptionData);

      if (responseDelete.code === '200') {
        queryClient.invalidateQueries({
          queryKey: ['administration-service-warehouses'],
        });
        setDeleteModal(false);
        showNotifications(
          NotificationType.SUCCESS,
          t('deleteModal.succedDelete')
        );
      } else {
        showNotifications(
          NotificationType.ERROR,
          t('deleteModal.errorDelete' + responseDelete.message)
        );
      }
    } catch (error) {
      showNotifications(NotificationType.ERROR, t('deleteModal.errorDelete'));
    } finally {
      setDeleteModal(false);
      setIsDeleting(false);
    }
  };

  return {
    session,
    columns,
    onDeleteConfirm,
    warehouseRows,
    deleteToggle,
    deleteModal,
  };
};
